import { useEffect, useState } from 'react';
import { io, type Socket } from 'socket.io-client';
import { useAuthStore } from '../store/authStore';

export type OccupancyLiveEvent = {
  sensorId: string;
  spotId: string | null;
  barrierId?: string | null;
  parkingId: string;
  isOccupied: boolean;
  targetType?: string;
};

/**
 * Connects to Socket.IO `/socket.io` and joins `tenant:{id}` room.
 */
export function useRealtime() {
  const tenantId = useAuthStore((s) => s.user?.tenantId ?? null);
  const [connected, setConnected] = useState(false);
  const [lastOccupancy, setLastOccupancy] = useState<OccupancyLiveEvent | null>(null);
  const [lastIncidentAt, setLastIncidentAt] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantId) return;

    const socket: Socket = io({
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join', tenantId);
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('occupancy', (ev: OccupancyLiveEvent) => {
      setLastOccupancy(ev);
    });
    socket.on('incident', () => {
      setLastIncidentAt(new Date().toISOString());
    });
    socket.on('session', () => {
      /* dashboard metrics refetch via react-query invalidate elsewhere if needed */
    });

    return () => {
      socket.emit('leave', tenantId);
      socket.disconnect();
    };
  }, [tenantId]);

  return { connected, lastOccupancy, lastIncidentAt };
}
